import React, { useEffect, useRef, useState } from "react";
import { LuChevronDown, LuX } from "react-icons/lu";
import { translateLabel, useTranslationSnapshot } from "./../translations/runtime";

const exportErrorLabels = new Map([
  ["Could not export products. Please try again.", "txt_web_vendor_export_products_failed"],
  ["The export did not return a file. Please try again.", "txt_web_vendor_export_file_missing"],
  ["Missing vendor session.", "txt_web_vendor_missing_vendor_session"],
]);


const EXPORT_COLUMNS = [
  ["productId", "Product ID"],
  ["productName", "Product Name"],
  ["skuId", "SKU ID"],
  ["skuCode", "SKU Code"],
  ["barcode", "Barcode"],
  ["sellingPrice", "Selling Price"],
  ["costPrice", "Cost Price"],
  ["stockQuantity", "Stock Quantity"],
  ["skuColor", "SKU Color"],
  ["currency", "Currency"],
];

const REQUIRED_COLUMNS = ["productId", "productName", "skuId"];

export default function ProductExportModal({ onClose, onExport }) {
  useTranslationSnapshot();
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [direction, setDirection] = useState("descending");
  const [columns, setColumns] = useState(EXPORT_COLUMNS.map(([key]) => key));
  const dialogRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  const handleExport = async () => {
    if (exporting) return;
    setExportError("");
    setExporting(true);
    try {
      await onExport();
      onClose();
    } catch (error) {
      setExportError(error?.message || "Could not export products. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.querySelector("button")?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus?.();
    };
  }, []);

  const handleKeyDown = (event) => {
    if (event.key === "Escape" && !exporting) onClose();
    if (event.key !== "Tab") return;
    const controls = dialogRef.current.querySelectorAll(
      "button:not(:disabled), input:not(:disabled), select:not(:disabled)",
    );
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const toggleColumn = (key) => {
    if (REQUIRED_COLUMNS.includes(key)) return;
    setColumns((current) =>
      current.includes(key)
        ? current.filter((column) => column !== key)
        : [...current, key],
    );
  };

  return (
    <div
      className="vendor-add-on-modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !exporting) onClose();
      }}
    >
      <div
        className="vendor-product-export-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="vendor-product-export-title"
        aria-busy={exporting}
        ref={dialogRef}
        onKeyDown={handleKeyDown}
      >
        <div className="vendor-product-export-header">
          <div>
            <h2 id="vendor-product-export-title">{translateLabel("txt_web_vendor_export_products", "Export Products")}</h2>
            {/* <p id="vendor-product-export-description">
              Choose the Excel columns you want to include.
            </p> */}
          </div>
          <button
            type="button"
            aria-label={translateLabel("btn_web_vendor_close_export_products", "Close Export Products")}
            disabled={exporting}
            onClick={onClose}
          >
            <LuX aria-hidden="true" />
          </button>
        </div>

        {/* <div className="vendor-product-export-banner">
          <strong>Export as Excel</strong>
          <span aria-live="polite">{columns.length} columns selected</span>
        </div>

        <div className="vendor-product-export-search">
          <div className="vendor-product-export-section-heading">
            <label htmlFor="vendor-product-export-search">Search</label>
            <button type="button" onClick={() => setSearch("")}>Clear</button>
          </div>
          <input
            id="vendor-product-export-search"
            type="search"
            placeholder="Search by product name, SKU, Category..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <section className="vendor-product-export-sort" aria-labelledby="vendor-product-export-sort-title">
          <h3 id="vendor-product-export-sort-title">Sort</h3>
          <div className="vendor-product-export-sort-controls">
            <div className="vendor-product-export-sort-by">
              <label htmlFor="vendor-product-export-sort">Sort by</label>
              <div className="vendor-product-export-select">
                <select id="vendor-product-export-sort" value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
                  <option value="newest">Newest</option>
                  <option value="productName">Product Name</option>
                  <option value="skuCode">SKU Code</option>
                  <option value="category">Category</option>
                </select>
                <LuChevronDown aria-hidden="true" />
              </div>
            </div>
            <fieldset className="vendor-product-export-direction">
              <legend>Direction</legend>
              <div>
                {["ascending", "descending"].map((value) => (
                  <label key={value}>
                    <input
                      type="radio"
                      name="vendor-product-export-direction"
                      value={value}
                      checked={direction === value}
                      onChange={() => setDirection(value)}
                    />
                    <span>{value === "ascending" ? "Ascending" : "Descending"}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>
        </section>

        <section className="vendor-product-export-columns" aria-labelledby="vendor-product-export-columns-title">
          <div className="vendor-product-export-section-heading">
            <h3 id="vendor-product-export-columns-title">Columns</h3>
            <div>
              <button type="button" onClick={() => setColumns(EXPORT_COLUMNS.map(([key]) => key))}>Select all</button>
              <button type="button" onClick={() => setColumns([...REQUIRED_COLUMNS])}>Clear</button>
            </div>
          </div>
          <div className="vendor-product-export-column-grid">
            {EXPORT_COLUMNS.map(([key, label]) => (
              <label
                key={key}
                className={REQUIRED_COLUMNS.includes(key) ? "vendor-product-export-column-required" : undefined}
              >
                <input
                  type="checkbox"
                  checked={columns.includes(key)}
                  disabled={REQUIRED_COLUMNS.includes(key)}
                  onChange={() => toggleColumn(key)}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </section> */}

        {exportError && (
          <p className="vendor-product-export-error" role="alert">{exportErrorLabels.has(exportError)
            ? translateLabel(exportErrorLabels.get(exportError), exportError)
            : exportError}</p>
        )}
        <div className="vendor-product-export-footer">
          <button
            type="button"
            className="vendor-product-export-cancel"
            disabled={exporting}
            onClick={onClose}
          > {translateLabel("btn_cancel", "Cancel")} </button>
          <button
            type="button"
            className="vendor-product-export-submit"
            disabled={exporting}
            onClick={handleExport}
          >
            {exporting ? translateLabel("txt_web_vendor_exporting_products", "Exporting...") : translateLabel("txt_web_vendor_export", "Export")}
          </button>
        </div>
      </div>
    </div>
  );
}
